import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Pencil, Send, ShieldCheck, CheckCircle2, AlertTriangle, LogOut as Exit, Link2 } from 'lucide-react';
import CareLayout from './CareLayout';
import { care, fail, rm, can } from './careApi';
import { Badge, Btn, Card, Loading, Table } from './careUi';
import FormEditor from './FormEditor';
import DocumentsTab from './DocumentsTab';
import BillingProfile from './BillingProfile';
import InvoiceDialog, { ReceiptActions } from './InvoiceDialog';
import { ask } from './AskDialog';
import SignLinkDialog from './SignLinkDialog';

const Detail = ({ label, v }) => (<div><p className="text-[11px] text-slate-400">{label}</p><p className="text-sm text-slate-800">{v || '—'}</p></div>);

const DetailsTab = ({ r }) => (
  <Card className="p-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="resident-details">
    <Detail label="Resident ID" v={r.resident_id} /><Detail label="NRIC / Passport" v={r.nric} /><Detail label="Date of birth" v={r.dob} />
    <Detail label="Gender" v={r.gender} /><Detail label="Contact" v={r.phone} /><Detail label="Email" v={r.email} />
    <Detail label="Admission date" v={r.admission_date} /><Detail label="Room" v={r.room} />
    <Detail label="Monthly billing" v={rm(r.monthly_amount)} /><Detail label="Address" v={r.address} />
    <Detail label="Representative" v={`${r.representative_name || ''}${r.representative_relationship ? ` (${r.representative_relationship})` : ''}`} />
    <Detail label="Representative contact" v={[r.representative_phone, r.representative_email].filter(Boolean).join(' · ')} />
    <Detail label="Registered by" v={r.created_by} /><Detail label="Approved by" v={r.approved_by} />
    {r.admission_exception && <div className="sm:col-span-2"><Detail label="Admission exception" v={`${r.admission_exception} — ${r.admission_exception_by}`} /></div>}
  </Card>
);

const InvoicesTab = ({ rid, open, tick }) => {
  const [rows, setRows] = useState([]);
  useEffect(() => { care.get('/invoices', { params: { resident_id: rid } }).then((x) => setRows(x.data)).catch(fail); }, [rid, tick]);
  return (
    <Card><Table testid="resident-invoices" rows={rows} onRow={(i) => open(i.id)} cols={[
      { k: 'invoice_no', h: 'Invoice' }, { k: 'period_label', h: 'Period' }, { k: 'due_date', h: 'Due' },
      { k: 'total', h: 'Total', right: true, r: (i) => rm(i.total) }, { k: 'balance', h: 'Balance', right: true, r: (i) => rm(i.balance) },
      { k: 'status', h: 'Status', r: (i) => <Badge s={i.status} /> }]} /></Card>
  );
};

const ReceiptsTab = ({ rid, tick, bump }) => {
  const [rows, setRows] = useState([]);
  useEffect(() => { care.get('/receipts', { params: { resident_id: rid } }).then((x) => setRows(x.data)).catch(fail); }, [rid, tick]);
  return (
    <Card><Table testid="resident-receipts" rows={rows} cols={[
      { k: 'receipt_no', h: 'Receipt' }, { k: 'receipt_date', h: 'Date' }, { k: 'invoice_no', h: 'Invoice' }, { k: 'method', h: 'Method' },
      { k: 'amount', h: 'Amount', right: true, r: (x) => rm(x.amount) }, { k: 'status', h: 'Status', r: (x) => <Badge s={x.status} /> },
      { k: 'a', h: '', right: true, r: (x) => <ReceiptActions rc={x} onChanged={bump} /> }]} /></Card>
  );
};

export default function CareResidentProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const [r, setR] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [tab, setTab] = useState('details');
  const [inv, setInv] = useState(null);
  const [tick, setTick] = useState(0);
  const [signOpen, setSignOpen] = useState(false);
  const load = useCallback(() => care.get(`/residents/${id}`).then((x) => setR(x.data)).catch(fail), [id]);
  useEffect(() => { load(); care.get('/form-templates').then((x) => setTpl(x.data)).catch(() => {}); }, [load]);
  const careRole = can('admin', 'staff');
  const finRole = can('admin', 'finance');

  const act = async (path, body, msg) => {
    try { const { data } = await care.post(`/residents/${id}/${path}`, body); setR(data); toast.success(msg); } catch (e) { fail(e); }
  };
  const exception = async () => { const note = await ask({ title: 'Record admission exception', message: 'Admission will be marked complete even though forms or documents are outstanding.', label: 'Reason', textarea: true, required: true, minLength: 5, confirm: 'Record exception' }); if (note) act('admission/exception', { note }, 'Exception recorded'); };
  const discharge = async () => { const note = await ask({ title: `Discharge ${r.name}`, label: 'Discharge note', textarea: true, danger: true, confirm: 'Discharge' }); if (note !== null) act('discharge', { note }, 'Resident discharged'); };

  if (!r) return <CareLayout><Loading /></CareLayout>;
  const tabs = [
    ['details', 'Resident Details', true], ['admission', 'Admission Form', careRole], ['pdpa', 'PDPA Consent', careRole],
    ['contract', 'Resident Contract', careRole], ['checklist', 'Admission Checklist', careRole],
    ['billing', 'Billing and Invoices', finRole], ['payments', 'Payments and Receipts', finRole], ['docs', 'Supporting Documents', careRole],
  ].filter((t) => t[2]);
  const p = r.progress || { forms: {} };

  return (
    <CareLayout>
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 mb-6">
        <div>
          <p className="font-mono text-xs text-teal-700 font-semibold" data-testid="profile-resident-id">{r.resident_id}</p>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="profile-name">{r.name}</h1>
          <div className="flex flex-wrap gap-2 mt-2"><Badge s={r.status} testid="profile-status" /><Badge s={r.admission_status} testid="profile-admission-status" /></div>
        </div>
        <div className="flex flex-wrap gap-2">
          {careRole && <Btn variant="ghost" onClick={() => setSignOpen(true)} data-testid="profile-sign-link"><Link2 className="w-4 h-4" />Family signing link</Btn>}
          {careRole && <Btn variant="ghost" onClick={() => nav(`/care/residents/${id}/edit`)} data-testid="profile-edit"><Pencil className="w-4 h-4" />Edit</Btn>}
          {careRole && r.status === 'Draft' && <Btn onClick={() => act('submit', undefined, 'Submitted for approval')} data-testid="profile-submit"><Send className="w-4 h-4" />Submit for approval</Btn>}
          {can('admin') && r.status === 'Pending Approval' && <Btn variant="dark" onClick={() => act('approve', undefined, 'Registration approved')} data-testid="profile-approve"><ShieldCheck className="w-4 h-4" />Approve</Btn>}
          {can('admin') && r.status === 'Active' && <Btn variant="ghost" onClick={discharge} data-testid="profile-discharge"><Exit className="w-4 h-4" />Discharge</Btn>}
        </div>
      </div>

      {careRole && r.admission_status === 'Incomplete' && (
        <Card className="p-4 mb-6 flex flex-col md:flex-row md:items-center justify-between gap-3 border-amber-200 bg-amber-50/50" data-testid="admission-progress">
          <div className="text-sm">
            <p className="font-semibold text-slate-800 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" />Admission not yet complete</p>
            <p className="text-slate-600 mt-1">
              {Object.entries(p.forms).map(([k, v]) => `${tpl?.labels?.[k] || k}: ${v}`).join(' · ')}
              {p.missing_docs?.length > 0 && <><br />Missing documents: {p.missing_docs.join(', ')}</>}
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Btn disabled={!p.can_complete} onClick={() => act('admission/complete', undefined, 'Admission completed')} data-testid="admission-complete"><CheckCircle2 className="w-4 h-4" />Complete admission</Btn>
            {can('admin') && <Btn variant="ghost" onClick={exception} data-testid="admission-exception">Record exception</Btn>}
          </div>
        </Card>
      )}

      <div className="flex gap-1 overflow-x-auto border-b border-slate-200 mb-6" data-testid="profile-tabs">
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-3.5 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === k ? 'border-teal-700 text-teal-800 font-semibold' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
            {l}{tpl?.forms?.[k] && p.forms[k] && <span className={`ml-1.5 inline-block w-1.5 h-1.5 rounded-full ${['Completed', 'Approved'].includes(p.forms[k]) ? 'bg-emerald-500' : 'bg-amber-400'}`} />}
          </button>
        ))}
      </div>

      {tab === 'details' && <DetailsTab r={r} />}
      {tpl && tpl.forms[tab] && <FormEditor rid={id} ft={tab} tpl={tpl.forms[tab]} company={tpl.company} label={tpl.labels[tab]} onChanged={load} />}
      {tab === 'billing' && <div className="space-y-6"><BillingProfile rid={id} tick={tick} onInvoice={(i) => { setInv(i.id); setTick(tick + 1); }} /><InvoicesTab rid={id} open={setInv} tick={tick} /></div>}
      {tab === 'payments' && <ReceiptsTab rid={id} tick={tick} bump={() => setTick(tick + 1)} />}
      {tab === 'docs' && tpl && <DocumentsTab rid={id} docTypes={tpl.doc_types} required={tpl.required_docs} onChanged={load} />}
      <InvoiceDialog id={inv} onClose={() => setInv(null)} onChanged={() => setTick((t) => t + 1)} />
      {careRole && <SignLinkDialog r={r} open={signOpen} onClose={() => { setSignOpen(false); load(); }} />}
    </CareLayout>
  );
}
